import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import {
  Router,
  NavigationEnd,
  Event as RouterEvent,
  ActivatedRoute,
} from "@angular/router";
import { distance } from "fastest-levenshtein";
import { lastValueFrom, Observable, of, Subscription } from "rxjs";
import { filter, map, distinctUntilChanged, startWith } from "rxjs/operators";
import { SideNavigatorService } from "../../services/side-navigator.service";
import { SidebarCollapseService } from "../../services/sidebar-collapse.service";
import { ScreenSizeService } from "src/app/services/screen-size.service";
import { FeatureService } from "src/app/services/features.service";
import { AuthService } from "../../services/auth.service";
import { CacheService } from "src/app/services/cache.service";
import { NetworkService } from "src/app/services/network.service";
import {
  ActiveOrganizationService,
  OrgSwitcherData,
  OrgSwitcherEntry,
} from "src/app/services/active-organization.service";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() docked: boolean = false; // Accept docked input parameter

  networkSubscription: Subscription | null = null;
  online = true;
  routerEventsSubscription: Subscription | null = null;

  userInfo$: Observable<any>;
  org$: Observable<any>;
  switchingOrg = false;
  switchTargetOrgName: string | null = null;

  features = [];
  orderedFeatures = [];
  currentFeature: any;
  isMobile: boolean;
  isDesktop: boolean;
  public featureIconUrlMap: { [key: string]: string } = {};
  // Always computed from current route
  get isLoginRoute(): boolean {
    return this.router.url.startsWith("/login");
  }

  constructor(
    public auth: AuthService,
    public sideNavigatorService: SideNavigatorService,
    public sidebarCollapseService: SidebarCollapseService,
    public screenSizeService: ScreenSizeService,
    public featureService: FeatureService,
    private router: Router,
    private route: ActivatedRoute,
    private cacheService: CacheService,
    private networkService: NetworkService,
    private cdr: ChangeDetectorRef,
    public activeOrgService: ActiveOrganizationService
  ) {}

  ngOnInit(): void {
    // No need to set isLoginRoute; it is computed from Router URL
    this.fetchUserData();

    // Initialize features initially
    this.initializeFeatures();

    // Listen to network status changes
    this.networkSubscription = this.networkService
      .getOnlineStatus()
      .subscribe((isOnline) => {
        // console.log("Network status event:", isOnline);
        if (this.online !== isOnline) {
          // console.log("Network status changed:", isOnline);
          this.online = isOnline;
          this.initializeFeatures();
        }
      });

    // Listen to screen size changes
    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.screenSizeService.isDesktop$.subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    // Subscribe to Router NavigationEnd events
    this.routerEventsSubscription = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // console.log("NavigationEnd event:", event);
        this.updateCurrentFeature(event.urlAfterRedirects);
      });

    // Trigger refresh when sidebar becomes visible due to route change (leaving login)
    const routeVisibilitySub = this.router.events
      .pipe(
        startWith(null as any), // evaluate once on init
        map(() => this.isLoginRoute),
        distinctUntilChanged()
      )
      .subscribe((isLogin) => {
        if (!isLogin) {
          // Sidebar visible by route -> ensure it's hydrated
          this.ensureActiveState();
        }
      });

    // Trigger refresh when user explicitly shows the side navigator
    const sideNavVisibilitySub = this.sideNavigatorService.hideSideNav$
      .pipe(
        distinctUntilChanged(),
        map((hidden) => !hidden)
      )
      .subscribe((visible) => {
        if (visible && !this.isLoginRoute) {
          this.ensureActiveState();
        }
      });

    // Track additional subs for cleanup
    if (this.routerEventsSubscription) {
      (this.routerEventsSubscription as any).add(routeVisibilitySub);
      (this.routerEventsSubscription as any).add(sideNavVisibilitySub);
    }

    // Listen for navigation changes from React Native features (e.g., protocols switching adult/pediatric)
    this.setupReactNavigationListener();

    // Listen for org membership changes from React Native features (e.g.
    // useradmin's Organizations panel, after accepting an invitation) so
    // the switcher picks up a newly-available org without a page reload
    this.setupOrgMembershipListener();

    // Same idea, but for the "Switch" button on useradmin's own Linked
    // Accounts row - it can't call this component's switchToLinkedAccount
    // directly (different framework/bundle), so it dispatches this event
    // with the target account instead and lets shell do the actual switch.
    this.setupLinkedAccountSwitchListener();
  }

  private setupLinkedAccountSwitchListener(): void {
    window.addEventListener("switchToLinkedAccount", (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (!detail.targetUserId) return;
      this.switchToLinkedAccount(
        detail.targetUserId,
        detail.label || detail.email || "your linked account"
      );
    });
  }

  private setupOrgMembershipListener(): void {
    window.addEventListener("orgMembershipChanged", () => {
      this.activeOrgService.refresh().catch((error) => {
        console.error(
          "Error refreshing org switcher data after orgMembershipChanged",
          error
        );
      });
    });
  }

  private setupReactNavigationListener(): void {
    const handleReactNavigationChange = (event: any) => {
      const { navigationParam, source, featureId } = event.detail || {};
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log("🔔 Sidebar: Received reactNavigationChange event");
      console.log("📍 navigationParam:", navigationParam);
      console.log("🔧 source:", source);
      console.log("🆔 featureId:", featureId);
      console.log("🌐 Current URL:", this.router.url);

      // Update the current route to reflect the new navigation parameter
      // This will trigger the sidebar to update which menu item is selected
      if (navigationParam) {
        // Construct the new URL with the updated query parameter
        const currentUrl = this.router.url.split("?")[0]; // Get base URL without query params
        const newUrl = `${currentUrl}?navigationParam=${encodeURIComponent(
          navigationParam
        )}&uuid=${featureId}`;
        console.log(
          "🔄 Sidebar: Updating current feature based on new URL:",
          newUrl
        );

        // Update the current feature to trigger highlighting
        this.updateCurrentFeature(newUrl);

        // Also sync the actual Angular route's query param - sub-menu-item
        // highlighting (endsWithRoute) reads live navigationParam from
        // this.route.snapshot, not from currentFeature, so without this the
        // icon never updates when the RN feature navigates internally
        // (e.g. its own footer/tab taps) instead of via a sidebar click.
        // replaceUrl avoids flooding browser history on every internal sync.
        this.router
          .navigate([], {
            relativeTo: this.route,
            queryParams: {
              navigationParam,
              uuid: featureId,
            },
            queryParamsHandling: "merge",
            replaceUrl: true,
          })
          .then(() => {
            // Trigger change detection to update the UI
            this.cdr.detectChanges();
            console.log(
              "✅ Sidebar: Updated current feature and triggered change detection"
            );
          });
      }
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    };

    window.addEventListener(
      "reactNavigationChange",
      handleReactNavigationChange
    );
    console.log("✅ Sidebar: Listening for reactNavigationChange events");
  }

  hideAndLogout(): void {
    try {
      this.sideNavigatorService.hide();
    } catch (e) {
      console.warn("Failed to hide sidebar before logout", e);
    }
    // Reset any sidebar-bound state before navigating away
    this.resetSidebarState();
    this.auth.logout();
  }

  private resetSidebarState(): void {
    try {
      // Clear streams consumed by the template
      this.userInfo$ = of(null);
      this.org$ = of(null);
      this.activeOrgService.clear();

      // Clear local UI state
      this.features = [];
      this.orderedFeatures = [];
      this.currentFeature = null;
      this.featureIconUrlMap = {};
      // isLoginRoute is computed; no manual assignment necessary

      // Unsubscribe from live streams
      if (this.networkSubscription) {
        this.networkSubscription.unsubscribe();
        this.networkSubscription = null;
      }
      // Keep routerEventsSubscription so we can re-evaluate when route changes

      // Trigger UI update
      try {
        this.cdr.detectChanges();
      } catch {}
    } catch (err) {
      console.warn("Error resetting sidebar state on logout", err);
    }
  }

  // Ensure sidebar data and subscriptions are active when not on login
  private ensureActiveState(): void {
    // Re-fetch user/org
    this.fetchUserData();
    // Re-initialize features
    this.initializeFeatures();
    // Re-create network subscription if needed
    if (!this.networkSubscription) {
      this.networkSubscription = this.networkService
        .getOnlineStatus()
        .subscribe((isOnline) => {
          // console.log("Network status event:", isOnline);
          if (this.online !== isOnline) {
            // console.log("Network status changed:", isOnline);
            this.online = isOnline;
            this.initializeFeatures();
          }
        });
    }
  }

  private async initializeFeatures(): Promise<void> {
    // Step 1: Fetch Feature Descriptions
    try {
      const featureData = await this.featureService.getFeatureDescriptions();
      // Show every feature in the nav regardless of offline support (same
      // reasoning as home.component's fetchAndOrderFeatures) - annotate
      // rather than drop, so the sidebar doesn't reshuffle/shrink while
      // offline, and the template can flag the unsupported ones instead.
      const isOnline = this.networkService.isOnline();
      this.features = featureData.map((f) => ({
        ...f,
        offlineUnsupported: f.offline !== "true" && !isOnline,
      }));
      // console.log("Feature Descriptions:", featureData);

      // Step 2: Determine the Current Feature
      const currentRoute = this.router.url;
      this.currentFeature = this.findBestMatchingFeature(currentRoute);

      // Step 3: Fetch User Information
      const userInfo = await this.auth.getUserInfo(); // Await the async result of getUserInfo
      // Step 4: Fetch Feature Order based on the ACTIVE organization - the
      // legacy organization_id field never changes on an org switch, so a
      // multi-org user who has switched away from it would 401 here (the
      // backend scopes /features/order/{id} to the caller's active org) and
      // get logged out by the auth interceptor's blanket 401 handling.
      const activeOrgId =
        userInfo.active_organization_id || userInfo.organization_id;
      try {
        const orderList = await this.featureService.getFeatureOrder(
          activeOrgId
        ); // Await feature order fetch
        // console.log("Order List:", orderList);

        // Create a map of feature_id to order for quick lookup
        const orderMap = new Map<string, number>();
        if (orderList && orderList["list"] && orderList["list"].length > 0) {
          orderList["list"].forEach((item) => {
            orderMap.set(item.feature_id, item.order);
          });
        } else if (orderList && orderList.notFound) {
          console.log(
            "Sidebar: No feature order exists yet (404) - will sort all features alphabetically"
          );
        }

        // If no custom order exists, sort all features alphabetically
        if (orderMap.size === 0) {
          this.orderedFeatures = [...this.features].sort((a, b) =>
            a.homeName.localeCompare(b.homeName)
          );
        } else {
          // Separate features into ordered and unordered arrays
          const orderedFeatures: any[] = [];
          const unorderedFeatures: any[] = [];

          this.features.forEach((feature) => {
            if (orderMap.has(feature.feature_id)) {
              orderedFeatures.push(feature);
            } else {
              unorderedFeatures.push(feature);
            }
          });

          // Sort orderedFeatures based on their specified order
          orderedFeatures.sort((a, b) => {
            return (
              (orderMap.get(a.feature_id) || 0) -
              (orderMap.get(b.feature_id) || 0)
            );
          });

          // Sort unorderedFeatures alphabetically by homeName
          unorderedFeatures.sort((a, b) =>
            a.homeName.localeCompare(b.homeName)
          );

          // Combine both arrays, with orderedFeatures first
          this.orderedFeatures = [...orderedFeatures, ...unorderedFeatures];
        }

        // console.log("Ordered Features:", this.orderedFeatures);
      } catch (error) {
        console.error("Failed to fetch feature order:", error);
        // Fallback: Sort all features alphabetically by homeName
        this.orderedFeatures = [...this.features].sort((a, b) =>
          a.homeName.localeCompare(b.homeName)
        );
        // console.log(
        //   "Features sorted alphabetically due to order fetch failure."
        // );
      }
    } catch (error) {
      console.error("Failed to fetch feature descriptions:", error);
      // Optionally handle feature descriptions fetch failure
    }
  }

  fetchUserData(): void {
    // Fetch user information
    this.auth
      .getUserInfo()
      .then((userInfo) => {
        // Set the resolved user info as an observable
        this.userInfo$ = of(userInfo);
      })
      .catch((error) => {
        console.error("Error fetching user info:", error);
        // Handle error if needed
      });

    // Fetch organization data
    this.auth
      .getOrganization()
      .then((organization) => {
        // Set the resolved organization as an observable
        this.org$ = of(organization);
      })
      .catch((error) => {
        console.error("Error fetching organization:", error);
        // Handle error if needed
      });

    // Fetch the unified org-switcher data (this account's orgs + linked accounts' orgs)
    this.activeOrgService.refresh().catch((error) => {
      console.error("Error fetching org switcher data:", error);
    });
  }

  hasMultipleOrgOptions(data: OrgSwitcherData | null): boolean {
    if (!data) return false;
    const linkedOrgCount = data.linked_accounts.reduce(
      (sum, account) => sum + account.orgs.length,
      0
    );
    return data.current_account.orgs.length + linkedOrgCount > 1;
  }

  async switchToOrganization(org: OrgSwitcherEntry): Promise<void> {
    if (this.switchingOrg || org.active) return;
    this.switchingOrg = true;
    this.switchTargetOrgName = org.organization_name || org.organization_id;
    try {
      const success = await this.activeOrgService.switchActiveOrganization(
        org.organization_id
      );
      if (success) {
        // Both auth.getOrganization() (the sidebar's org$ / display name)
        // and auth.getUserInfo() (which getOrganization reads
        // active_organization_id from) cache to Filesystem/localStorage and
        // read that cache first - without clearing both, the reload below
        // would just redisplay the org we switched away from.
        await Promise.all([
          this.auth.clearOrganization(),
          this.auth.clearUserInfo(),
          // features-list is a single org-agnostic native cache key with
          // no other invalidation path - without clearing it, the reload
          // below would keep serving the org we switched away from.
          this.featureService.clearPersistedFeaturesCache(),
        ]);
        // Re-fetch userInfo now, before reloading, rather than leaving the
        // cache empty for the reloaded page to repopulate. Not required for
        // correctness (isLoggedIn() no longer treats a momentarily-empty
        // userInfo cache as a corrupted session), but it avoids an extra
        // network round-trip after reload and a brief flash of the old org
        // before the reloaded page's own fetch resolves.
        try {
          await this.auth.getUserInfo();
        } catch (error) {
          console.error(
            "Failed to pre-fetch userInfo before org-switch reload:",
            error
          );
        }
        // Full reload so every permanently-mounted feature bundle picks up
        // the new org context. Deliberately leave switchingOrg/the overlay
        // on through the reload rather than resetting it below - there's
        // no "success" moment worth showing before the page unloads, and
        // resetting first would just flash the overlay away early.
        window.location.reload();
        return;
      }
      // Switch failed (no exception, just success:false) - let the user try again
      this.switchingOrg = false;
      this.switchTargetOrgName = null;
    } catch (error) {
      console.error("Error switching organization:", error);
      this.switchingOrg = false;
      this.switchTargetOrgName = null;
    }
  }

  // Re-pull the switcher's org/linked-account list right as the menu opens,
  // so changes made elsewhere (e.g. accepting an org invitation in
  // useradmin) show up without needing a full page reload first.
  onOrgMenuOpened(): void {
    this.activeOrgService.refresh().catch((error) => {
      console.error("Error refreshing org switcher data:", error);
    });
  }

  // Seamless, like switchToOrganization above - no confirm() dialog, no
  // logout/re-login. A linked account is a different login/email but the
  // same person, and the linkage was already verified when it was set up,
  // so switching to it should feel the same as switching active org.
  async switchToLinkedAccount(
    targetUserId: string,
    label: string
  ): Promise<void> {
    if (this.switchingOrg) return;
    this.switchingOrg = true;
    this.switchTargetOrgName = label;
    try {
      const success = await this.auth.switchToLinkedAccount(targetUserId);
      if (success) {
        try {
          await this.auth.getUserInfo();
        } catch (error) {
          console.error(
            "Failed to pre-fetch userInfo before linked-account switch reload:",
            error
          );
        }
        window.location.reload();
        return;
      }
      this.switchingOrg = false;
      this.switchTargetOrgName = null;
    } catch (error) {
      console.error("Error switching to linked account:", error);
      this.switchingOrg = false;
      this.switchTargetOrgName = null;
    }
  }

  ngOnDestroy(): void {
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
    // Unsubscribe from screen size observables if stored as subscriptions
    // (Assuming you stored them, otherwise manage accordingly)
  }

  navigateToHome(): void {
    this.router.navigate(["/home"]);
    this.currentFeature = this.findBestMatchingFeature("/home");
    this.sideNavigatorService.toggleSideNavigator();
  }

  navigateToFeature(featureId: string, path: string, queryParam: string): void {
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("📱 Sidebar: navigateToFeature called");
    console.log("🆔 featureId:", featureId);
    console.log("📂 path:", path);
    console.log("🔗 queryParam:", queryParam);
    console.log("🌐 Current URL:", this.router.url);

    // Find the feature to get its baseUrl
    const feature = this.features.find((f) => f.feature_id === featureId);
    if (feature && feature.baseUrl) {
      // Set feature-base-path in sessionStorage so react-native-bundle can load the feature
      console.log(
        "💾 Sidebar: Setting feature-base-path in sessionStorage:",
        feature.baseUrl
      );
      sessionStorage.setItem("feature-base-path", feature.baseUrl);
    }

    // Check if we're already on the React Native route for this feature
    const currentUrl = this.router.url;
    const targetPath = `/react-native/${path.replace(/\//g, "")}`;
    console.log("🎯 Target path:", targetPath);
    console.log("✅ Already on route?", currentUrl.startsWith(targetPath));

    if (
      currentUrl === targetPath ||
      currentUrl.startsWith(targetPath + "?") ||
      currentUrl.startsWith(targetPath + "/")
    ) {
      // Already on this React Native feature, just dispatch navigation event
      console.log(
        "[NAV] 🚫 Sidebar: Already on React Native route, dispatching event only (NO NAVIGATION)"
      );

      // Update query parameters to trigger highlighting (same as footer)
      console.log("[NAV] 🔄 Sidebar: Updating query params for highlighting");

      // Construct the new URL with updated query parameter for immediate highlighting
      const currentUrlBase = this.router.url.split("?")[0];
      const newUrl = `${currentUrlBase}?navigationParam=${encodeURIComponent(
        queryParam
      )}&uuid=${featureId}`;
      console.log(
        "[NAV] 🔄 Sidebar: Updating currentFeature based on new URL:",
        newUrl
      );

      // Update currentFeature immediately for sidebar highlighting
      this.updateCurrentFeature(newUrl);

      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            navigationParam: queryParam,
            uuid: featureId,
            source: "sidebar",
          },
          queryParamsHandling: "merge",
          replaceUrl: false,
        })
        .then(() => {
          console.log("[NAV] ✅ Sidebar: Query params updated");

          // Dispatch the navigation event
          const navigationEvent = new CustomEvent("angularNavigationChange", {
            detail: {
              navigationParam: queryParam,
              featureId: path.replace(/\//g, ""),
              source: "sidebar",
            },
          });
          console.log(
            "[NAV] 📤 Sidebar: About to dispatch event with detail:",
            {
              navigationParam: queryParam,
              featureId: path.replace(/\//g, ""),
              source: "sidebar",
            }
          );
          window.dispatchEvent(navigationEvent);
          console.log("[NAV] ✅ Sidebar: Event dispatched successfully");

          // Trigger change detection to update UI
          this.cdr.detectChanges();
          this.sideNavigatorService.toggleSideNavigator();
        });
    } else {
      // Navigate to the React Native route while preserving Angular history
      // so the back button returns to the previous feature instead of home.
      console.log("[NAV] 🚀 Sidebar: Navigating to new React Native route");
      console.log("[NAV] 📍 Sidebar: Navigating to:", [
        "/react-native",
        path.replace(/\//g, ""),
      ]);
      console.log("[NAV] 🔑 Sidebar: With queryParams:", {
        navigationParam: queryParam,
        uuid: featureId,
        source: "initial",
      });
      this.router
        .navigate(["/react-native", path.replace(/\//g, "")], {
          queryParams: {
            navigationParam: queryParam,
            uuid: featureId,
            source: "initial",
          },
          replaceUrl: false,
        })
        .then(() => {
          this.currentFeature = this.findBestMatchingFeature(path);
          this.sideNavigatorService.toggleSideNavigator();
        });
    }
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  }

  async getFeatureIconUrl(feature: any): Promise<void> {
    const iconUrl = `${feature.baseUrl}${feature.homeIcon}`;

    let computedUrl: string = "";

    const img = await lastValueFrom(
      this.cacheService.getResource(iconUrl, true)
    );

    // now destructure into `data` and `content`
    const { data, contentType: content } = img;

    if (data) {
      if (typeof data === "string" && data.startsWith("data:image")) {
        computedUrl = data;
        // } else if (img instanceof Blob) {
        //   computedUrl = URL.createObjectURL(img);
      } else {
        computedUrl = data as string;
      }
    } else {
      computedUrl = "";
    }

    this.featureIconUrlMap[iconUrl] = computedUrl;
    // Notify Angular to update change detection
    // this.cdr.markForCheck();
  }

  getIcon(feature: any): string {
    const iconUrl = feature.baseUrl + feature.homeIcon;
    if (!this.featureIconUrlMap[iconUrl]) {
      // Trigger asynchronous loading if icon is not available yet
      this.getFeatureIconUrl(feature);
      return ""; // Return empty string until the icon URL is loaded
    }
    return this.featureIconUrlMap[iconUrl];
  }

  onImageError() {
    this.networkService
      .checkInternetConnection()
      .then((isConnected) => {
        if (isConnected) {
          // console.log("Internet is active");
        } else {
          this.networkService.markInternetDown();
        }
      })
      .catch((error) =>
        console.error("Unexpected error in connectivity check:", error)
      );
  }

  findBestMatchingFeature(currentRoute: string): any {
    // Normalize helpers
    const stripRN = (p: string) =>
      (p || "").replace(/\/react-native(\/|$)/, "/");
    const normalizePath = (full: string) => {
      try {
        // If full is a full URL, use URL; otherwise treat as path
        const u = full.startsWith("http")
          ? new URL(full)
          : new URL(full, window.location.origin);
        return stripRN(u.pathname);
      } catch {
        // Fallback: strip query manually
        return stripRN(full.split("?")[0]);
      }
    };
    const getNavParam = (full: string) => {
      try {
        const u = full.startsWith("http")
          ? new URL(full)
          : new URL(full, window.location.origin);
        const v = u.searchParams.get("navigationParam");
        return v ? decodeURIComponent(v) : null;
      } catch {
        const q = full.split("?")[1] || "";
        const params = new URLSearchParams(q);
        const v = params.get("navigationParam");
        return v ? decodeURIComponent(v) : null;
      }
    };

    const basePath = normalizePath(currentRoute);
    const navParam = getNavParam(currentRoute);

    // 1) Strong match: basePath must equal feature.defaultPath (normalized).
    //    If navParam exists, it must also match one of feature.footerIcons[].path
    for (const feature of this.features) {
      const featureBase = normalizePath(feature.defaultPath);
      const baseMatches = basePath === featureBase;
      if (!baseMatches) continue;

      if (navParam) {
        const icons = Array.isArray(feature.footerIcons)
          ? feature.footerIcons
          : [];
        const hasNav = icons.some((ic: any) => {
          // Compare decoded navigationParam to icon.path as-is (both expected to be route-ish strings)
          return ic && typeof ic.path === "string" && ic.path === navParam;
        });
        if (hasNav) return feature;
        // If navParam provided but no matching footer icon, skip this feature
        continue;
      }
      // No navParam required, accept base match
      return feature;
    }

    // 2) Fallback: distance-based best path match (ignoring navigationParam)
    let bestMatch = null;
    let bestDistance = Infinity;
    for (const feature of this.features) {
      const dist = distance(stripRN(basePath), stripRN(feature.defaultPath));
      if (dist < bestDistance) {
        bestDistance = dist;
        bestMatch = feature;
      }
    }

    // 3) Final fallback: home
    const distHome = distance(basePath, "/home");
    if (distHome < bestDistance) {
      bestMatch = { name: "Home", defaultPath: "/home" };
    }

    return bestMatch;
  }

  findBestMatchingSubFeature(options): any {
    let bestMatch = null;
    let bestDistance = Infinity;
    const currentRoute = this.router.url;

    options.forEach((option) => {
      const dist = distance(currentRoute, option.name);

      if (dist < bestDistance) {
        bestDistance = dist;
        bestMatch = option;
      }
    });

    return bestMatch;
  }

  endsWithRoute(route: string): boolean {
    // Use ActivatedRoute to get current navigationParam, same as footer component
    const navigationParam =
      this.route.snapshot.queryParamMap.get("navigationParam");
    return navigationParam === route;
  }

  filterFeaturesForScreenSize() {
    if (this.isDesktop) {
      // If we are on a desktop, return all features
      return this.orderedFeatures;
    }

    // If not on desktop, filter out features with desktopOnly = true
    return this.orderedFeatures.filter((feature) => !feature.desktopOnly);
  }

  // New method to update current feature based on navigation
  private updateCurrentFeature(newRoute: string): void {
    this.currentFeature = this.findBestMatchingFeature(newRoute);
    // console.log("Updated Current Feature:", this.currentFeature);
  }
}
